import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import DefaultFooter from "../components/default-footer";
import Header1 from "../components/default-header";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { API_URL } from "../config";
import Header from "../components/header";

// ── Types pour l'API Kayak ──────────────────────────────────────────────────
interface KayakLocation {
  id: string;
  objectID?: string;
  kayakId?: string;
  displayname: string;
  loctype: string;
  name: string;
  cityname: string;
  country: string;
  ap: string;
  apicode: string;
  isMetroOnly?: boolean;
  shortdisplayname?: string;
  searchFormPrimary?: string;
  searchFormSecondary?: string;
  secondary?: string;
  displayType?: {
    type: string;
    displayName: string;
  };
}

// ── Liste complète des nationalités ─────────────────────────────────────────
const NATIONALITIES = [
  "Afghan", "Albanian", "Algerian", "American", "Andorran", "Angolan",
  "Argentine", "Armenian", "Australian", "Austrian", "Azerbaijani",
  "Bahamian", "Bahraini", "Bangladeshi", "Barbadian", "Belarusian",
  "Belgian", "Belizean", "Beninese", "Bhutanese", "Bolivian", "Bosnian",
  "Botswanan", "Brazilian", "British", "Bruneian", "Bulgarian",
  "Burkinabe", "Burmese", "Burundian", "Cambodian", "Cameroonian",
  "Canadian", "Cape Verdean", "Central African", "Chadian", "Chilean",
  "Chinese", "Colombian", "Comoran", "Congolese", "Costa Rican",
  "Croatian", "Cuban", "Cypriot", "Czech", "Danish", "Djiboutian",
  "Dominican", "Dutch", "East Timorese", "Ecuadorian", "Egyptian",
  "Emirati", "English", "Equatorial Guinean", "Eritrean", "Estonian",
  "Eswatini", "Ethiopian", "Fijian", "Filipino", "Finnish", "French",
  "Gabonese", "Gambian", "Georgian", "German", "Ghanaian", "Greek",
  "Grenadian", "Guatemalan", "Guinean", "Guinea-Bissauan", "Guyanese",
  "Haitian", "Honduran", "Hungarian", "Icelandic", "Indian",
  "Indonesian", "Iranian", "Iraqi", "Irish", "Israeli", "Italian",
  "Ivorian", "Jamaican", "Japanese", "Jordanian", "Kazakhstani",
  "Kenyan", "Kiribati", "Kosovar", "Kuwaiti", "Kyrgyz", "Laotian",
  "Latvian", "Lebanese", "Basotho", "Liberian", "Libyan",
  "Liechtensteiner", "Lithuanian", "Luxembourgish", "Macedonian",
  "Malagasy", "Malawian", "Malaysian", "Maldivian", "Malian", "Maltese",
  "Marshallese", "Mauritanian", "Mauritian", "Mexican", "Micronesian",
  "Moldovan", "Monacan", "Mongolian", "Montenegrin", "Moroccan",
  "Mozambican", "Namibian", "Nauruan", "Nepalese", "New Zealander",
  "Nicaraguan", "Nigerien", "Nigerian", "North Korean", "Norwegian",
  "Omani", "Pakistani", "Palauan", "Palestinian", "Panamanian",
  "Papua New Guinean", "Paraguayan", "Peruvian", "Polish", "Portuguese",
  "Qatari", "Romanian", "Russian", "Rwandan", "Saint Lucian",
  "Salvadoran", "Samoan", "San Marinese", "Sao Tomean", "Saudi Arabian",
  "Scottish", "Senegalese", "Serbian", "Seychellois", "Sierra Leonean",
  "Singaporean", "Slovak", "Slovenian", "Solomon Islander", "Somali",
  "South African", "South Korean", "South Sudanese", "Spanish",
  "Sri Lankan", "Sudanese", "Surinamese", "Swazi", "Swedish", "Swiss",
  "Syrian", "Taiwanese", "Tajik", "Tanzanian", "Thai", "Togolese",
  "Tongan", "Trinidadian", "Tunisian", "Turkish", "Turkmen", "Tuvaluan",
  "Ugandan", "Ukrainian", "Uruguayan", "Uzbekistani", "Vanuatuan",
  "Vatican", "Venezuelan", "Vietnamese", "Welsh", "Yemeni", "Zambian",
  "Zimbabwean",
];
const NATIONALITY_OPTIONS = NATIONALITIES.map((n) => ({ value: n, label: n }));

const MONTH_OPTIONS = [
  { value: "01", label: "January" }, { value: "02", label: "February" },
  { value: "03", label: "March" }, { value: "04", label: "April" },
  { value: "05", label: "May" }, { value: "06", label: "June" },
  { value: "07", label: "July" }, { value: "08", label: "August" },
  { value: "09", label: "September" }, { value: "10", label: "October" },
  { value: "11", label: "November" }, { value: "12", label: "December" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 26 }, (_, i) => {
  const y = String(CURRENT_YEAR + i);
  return { value: y, label: y };
});

// ── Validation ───────────────────────────────────────────────────────────
const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,50}$/;
const PASSPORT_REGEX = /^[A-Za-z0-9]{5,15}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s-]{7,16}$/;

type TextFieldName =
  | "from"
  | "to"
  | "firstName"
  | "lastName"
  | "nationality"
  | "passportNumber"
  | "passportExpiryMonth"
  | "passportExpiryYear"
  | "email"
  | "phone";

const STEP1_FIELDS: TextFieldName[] = ["from", "to"];
const STEP2_FIELDS: TextFieldName[] = [
  "firstName",
  "lastName",
  "nationality",
  "passportNumber",
  "passportExpiryMonth",
  "passportExpiryYear",
];
const STEP3_FIELDS: TextFieldName[] = ["email", "phone"];

// Retourne un message d'erreur (chaîne vide si le champ est valide)
const validateField = (name: TextFieldName, rawValue: string): string => {
  const value = (rawValue || "").trim();

  switch (name) {
    case "from":
      return value ? "" : "errorSelectFrom";
    case "to":
      return value ? "" : "errorSelectTo";

    case "firstName":
    case "lastName":
      if (!value) return "errorRequired";
      if (!NAME_REGEX.test(value)) return "errorNameLetters";
      return "";

    case "nationality":
      return value ? "" : "errorSelectNationality";

    case "passportNumber":
      if (!value) return "errorRequired";
      if (!PASSPORT_REGEX.test(value)) return "errorPassportInvalid";
      return "";

    case "passportExpiryMonth":
      return value ? "" : "errorSelectMonth";
    case "passportExpiryYear":
      return value ? "" : "errorSelectYear";

    case "email":
      if (!value) return "errorRequired";
      if (!EMAIL_REGEX.test(value)) return "errorEmailInvalid";
      return "";

    case "phone":
      if (!value) return "errorRequired";
      if (!PHONE_REGEX.test(value)) return "errorPhoneInvalid";
      return "";

    default:
      return "";
  }
};

// ── Styles CSS complets ─────────────────────────────────────────────────────
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Outfit:wght@300;400;500;600;700&display=swap');

    .rq-page { background: #f7f4ee; }

    /* ── Hero ── */
    .rq-hero {
      background: linear-gradient(135deg, #1a3240 0%, #294b5a 55%, #3a6272 100%);
      padding: 64px 0 100px;
      position: relative;
      overflow: hidden;
    }
    .rq-hero::before {
      content: '';
      position: absolute;
      inset: 0;
      background-image:
        radial-gradient(circle at 80% 40%, rgba(211,190,118,.14) 0%, transparent 50%),
        radial-gradient(circle at 15% 80%, rgba(211,190,118,.07) 0%, transparent 40%);
      pointer-events: none;
    }
    .rq-hero__decor {
      position: absolute;
      right: 5%;
      top: 50%;
      transform: translateY(-50%);
      font-size: 200px;
      opacity: .05;
      pointer-events: none;
      user-select: none;
      line-height: 1;
    }
    .rq-hero__breadcrumb {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: rgba(255,255,255,.45);
      margin-bottom: 20px;
    }
    .rq-hero__breadcrumb a { color: rgba(255,255,255,.45); text-decoration: none; }
    .rq-hero__breadcrumb a:hover { color: #d3be76; }
    .rq-hero__breadcrumb span { color: #d3be76; }
    .rq-hero__eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      font-size: 11px;
      font-weight: 600;
      color: #d3be76;
      margin-bottom: 12px;
    }
    .rq-hero__eyebrow::before,
    .rq-hero__eyebrow::after {
      content: '';
      display: block;
      width: 22px;
      height: 1px;
      background: #d3be76;
      opacity: .6;
    }
    .rq-hero__title {
      font-size: 22px;
      font-weight: 700;
      color: #fff;
      line-height: 1.1;
      margin-bottom: 10px;
    }
    .rq-hero__title strong { font-weight: 700; color: #d3be76; }
    .rq-hero__sub {
      font-size: 15px;
      color: rgba(255,255,255,.5);
      font-weight: 300;
      max-width: 500px;
    }

    /* ── Main layout ── */
    .rq-layout {
      display: grid;
      grid-template-columns: 1fr 340px;
      gap: 28px;
      padding: 0 0 80px;
      margin-top: -56px;
      position: relative;
      z-index: 10;
      align-items: start;
    }

    /* ── Form card ── */
    .rq-card {
      background: #fff;
      border-radius: 18px;
      box-shadow: 0 20px 64px rgba(41,75,90,.13);
      overflow: visible;
    }
    .rq-card__head {
      background: linear-gradient(90deg, #294b5a 0%, #3a6272 100%);
      padding: 20px 32px;
      display: flex;
      align-items: center;
      gap: 14px;
      border-top-left-radius: 18px;
      border-top-right-radius: 18px;
    }
    .rq-card__head-icon {
      width: 42px; height: 42px;
      background: rgba(211,190,118,.2);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      flex-shrink: 0;
    }
    .rq-card__head-title {
      font-size: 20px;
      font-weight: 600;
      color: #fff;
    }
    .rq-card__head-sub {
      font-size: 12px;
      color: rgba(255,255,255,.5);
      margin-top: 2px;
    }
    .rq-card__body { padding: 32px; }

    /* ── Step indicator ── */
    .rq-steps {
      display: flex;
      align-items: center;
      gap: 0;
      margin-bottom: 32px;
    }
    .rq-step {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;
    }
    .rq-step:last-child { flex: none; }
    .rq-step__num {
      width: 30px; height: 30px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 700;
      flex-shrink: 0;
      transition: all .2s;
    }
    .rq-step__num.done { background: #294b5a; color: #fff; }
    .rq-step__num.active { background: #d3be76; color: #1a3240; }
    .rq-step__num.pending { background: #f0ece4; color: #9c9488; }
    .rq-step__label {
      font-size: 11px;
      font-weight: 600;
      white-space: nowrap;
    }
    .rq-step__label.done { color: #294b5a; }
    .rq-step__label.active { color: #294b5a; }
    .rq-step__label.pending { color: #b0ab9f; }
    .rq-step__line {
      flex: 1;
      height: 2px;
      background: #f0ece4;
      margin: 0 8px;
      border-radius: 1px;
    }
    .rq-step__line.done { background: #294b5a; }

    /* ── Section title ── */
    .rq-section {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
      margin-top: 8px;
    }
    .rq-section__bar {
      width: 3px;
      height: 20px;
      background: #d3be76;
      border-radius: 2px;
      flex-shrink: 0;
    }
    .rq-section__title {
      font-size: 18px;
      font-weight: 600;
      color: #294b5a;
    }

    /* ── Field ── */
    .rq-field { display: flex; flex-direction: column; gap: 6px; position: relative; }
    .rq-field__label {
      font-size: 11px;
      font-weight: 600;
      color: #294b5a;
    }
    .rq-field__label span { color: #d3be76; margin-left: 2px; }
    
    .rq-field__input,
    .rq-field__select,
    .rq-field__textarea,
    .custom_input-picker {
      font-size: 14px;
      color: #294b5a;
      background: #f7f4ee;
      border: 1.5px solid #ede9e0 !important;
      border-radius: 10px !important;
      padding: 12px 16px !important;
      outline: none;
      transition: border-color .2s, box-shadow .2s;
      width: 100%;
      font-family: 'Outfit', sans-serif;
      box-sizing: border-box;
    }
    
    .rq-field__input::placeholder,
    .rq-field__textarea::placeholder { color: #b0ab9f; font-weight: 400; }
    
    .rq-field__input:focus,
    .rq-field__select:focus,
    .rq-field__textarea:focus,
    .custom_input-picker:focus {
      border-color: #294b5a !important;
      box-shadow: 0 0 0 3px rgba(41,75,90,.07);
      background: #fff;
    }
    
    .rq-field__select { 
      cursor: pointer; 
      appearance: none; 
      background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%23294b5a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E"); 
      background-repeat: no-repeat; 
      background-position: right 14px center; 
      padding-right: 36px; 
      max-height: 240px;
    }
    
    .rq-field__textarea { resize: vertical; min-height: 100px; line-height: 1.6; }

    /* ── Validation states ── */
    .rq-field__input.invalid,
    .rq-field__select.invalid,
    .rq-field__textarea.invalid,
    .custom_input-picker.invalid,
    .rq-select-trigger.invalid {
      border-color: #c0392b !important;
      background: #fdf2f0 !important;
    }
    .rq-field__input.invalid:focus,
    .rq-select-trigger.invalid:focus,
    .rq-select-trigger.invalid.open {
      box-shadow: 0 0 0 3px rgba(192,57,43,.12) !important;
    }
    .rq-field__error {
      font-size: 11.5px;
      color: #c0392b;
      margin: 0;
    }
    .rq-form-alert {
      background: #fdf2f0;
      border: 1px solid #f1b9ae;
      color: #9b2f22;
      padding: 12px 16px;
      border-radius: 10px;
      margin-bottom: 22px;
      font-size: 13.5px;
    }
    .rq-terms-row.invalid {
      background: #fdf2f0;
      border-radius: 10px;
      padding: 8px 10px;
      margin: -8px -10px 24px;
    }

    /* ── Custom dropdown select ── */
    .rq-select-trigger {
      font-size: 14px;
      color: #294b5a;
      background: #f7f4ee;
      border: 1.5px solid #ede9e0;
      border-radius: 10px;
      padding: 12px 16px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      transition: border-color .2s, box-shadow .2s;
      font-family: 'Outfit', sans-serif;
    }
    .rq-select-trigger.placeholder { color: #b0ab9f; }
    .rq-select-trigger.open,
    .rq-select-trigger:focus {
      border-color: #294b5a;
      box-shadow: 0 0 0 3px rgba(41,75,90,.07);
      background: #fff;
    }
    .rq-select-chevron {
      width: 11px; height: 8px; flex-shrink: 0;
      color: #294b5a;
      transition: transform .2s;
    }
    .rq-select-chevron.open { transform: rotate(180deg); }
    .rq-select-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      margin-top: 4px;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0,0,0,.15);
      z-index: 1000;
      overflow: hidden;
    }
    .rq-select-search {
      width: 100%;
      box-sizing: border-box;
      border: none;
      border-bottom: 1px solid #f0ece4;
      padding: 11px 16px;
      font-size: 13px;
      outline: none;
      font-family: 'Outfit', sans-serif;
      color: #294b5a;
    }
    .rq-select-list { max-height: 220px; overflow-y: auto; padding: 6px 0; }
    .rq-select-option {
      padding: 9px 16px;
      font-size: 13.5px;
      color: #294b5a;
      cursor: pointer;
      transition: background-color .15s ease;
    }
    .rq-select-option:hover,
    .rq-select-option.active { background-color: #f4f6f8; }
    .rq-select-option.active { font-weight: 600; }
    .rq-select-empty { padding: 14px 16px; font-size: 13px; color: #9c9488; text-align: center; }

    /* DatePicker container */
    .rq-datepicker { width: 100%; }
    .rq-datepicker .rmdp-container { width: 100%; }
    .rq-datepicker .custom_container-picker { width: 100%; }

    /* Two-select expiry row */
    .rq-expiry-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }

    /* ── KAYAK AUTOCOMPLETE DROPDOWN STYLE ── */
    .kayak-dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: #ffffff;
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
      border: 1px solid #e0e0e0;
      z-index: 1000;
      max-height: 320px;
      overflow-y: auto;
      margin-top: 4px;
      padding: 8px 0;
    }
    .kayak-dropdown-item {
      display: flex;
      align-items: center;
      padding: 10px 16px;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }
    .kayak-dropdown-item:hover {
      background-color: #f4f6f8;
    }
    .kayak-sub-item {
      padding-left: 28px;
    }
    .kayak-arrow-icon {
      width: 14px;
      height: 14px;
      margin-right: 8px;
      color: #1a1a1a;
      flex-shrink: 0;
    }
    .kayak-icon-box {
      width: 36px;
      height: 36px;
      background-color: #f0f2f5;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      flex-shrink: 0;
    }
    .kayak-icon-box svg {
      width: 18px;
      height: 18px;
      fill: #4a5568;
    }
    .kayak-info {
      display: flex;
      flex-direction: column;
    }
    .kayak-title-row {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .kayak-main-title {
      font-size: 14px;
      font-weight: 700;
      color: #1a1a1a;
    }
    .kayak-iata-code {
      font-size: 12px;
      font-weight: 600;
      color: #8c9ba5;
    }
    .kayak-subtitle {
      font-size: 12px;
      color: #6b7280;
      margin-top: 2px;
    }

    /* ── Grid helpers ── */
    .rq-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .rq-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
    .rq-mb { margin-bottom: 20px; }

    /* ── Trip type toggle ── */
    .rq-toggle {
      display: flex;
      background: #f0ece4;
      border-radius: 10px;
      padding: 4px;
      gap: 4px;
    }
    .rq-toggle__btn {
      flex: 1;
      padding: 10px 0;
      text-align: center;
      font-size: 13px;
      font-weight: 500;
      color: #9c9488;
      background: none;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      transition: all .2s;
    }
    .rq-toggle__btn.active {
      background: #294b5a;
      color: #fff;
      box-shadow: 0 2px 8px rgba(41,75,90,.2);
    }

    /* ── Passenger row ── */
    .rq-passenger-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 0;
      border-bottom: 1px solid #f0ece4;
    }
    .rq-passenger-row:last-child { border-bottom: none; }
    .rq-passenger-row__name { font-size: 14px; font-weight: 500; color: #294b5a; }
    .rq-passenger-row__sub { font-size: 12px; color: #9c9488; margin-top: 2px; }
    .rq-passenger-row__ctrl { display: flex; align-items: center; gap: 12px; }
    .rq-pax-btn {
      width: 32px; height: 32px;
      border: 1.5px solid #ede9e0;
      border-radius: 50%;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #294b5a;
      font-size: 16px;
      font-weight: 700;
      transition: all .2s;
      line-height: 1;
    }
    .rq-pax-btn:hover:not(:disabled) { background: #294b5a; border-color: #294b5a; color: #fff; }
    .rq-pax-btn:disabled { opacity: .3; cursor: not-allowed; }
    .rq-pax-val { font-size: 16px; font-weight: 600; color: #294b5a; min-width: 20px; text-align: center; }

    /* ── Divider ── */
    .rq-divider { height: 1px; background: #f0ece4; margin: 24px 0; }

    /* ── Submit button ── */
    .rq-submit {
      background: linear-gradient(90deg, #d3be76, #b09a50);
      color: #1a3240;
      border: none;
      border-radius: 12px;
      padding: 16px 40px;
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 10px;
      letter-spacing: .04em;
      transition: all .25s;
      width: 100%;
      justify-content: center;
      box-shadow: 0 4px 16px rgba(211,190,118,.3);
    }
    .rq-submit:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 28px rgba(211,190,118,.45);
    }
    .rq-submit:disabled {
      opacity: .7;
      cursor: not-allowed;
      transform: none;
      box-shadow: 0 4px 16px rgba(211,190,118,.3);
    }
    @keyframes rqSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    /* ── Success ── */
    .rq-success {
      text-align: center;
      padding: 48px 32px;
    }
    .rq-success__icon {
      width: 72px; height: 72px;
      background: linear-gradient(135deg, rgba(41,75,90,.1), rgba(211,190,118,.15));
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 32px;
      margin: 0 auto 20px;
    }
    .rq-success__title {
      font-size: 28px;
      font-weight: 600;
      color: #294b5a;
      margin-bottom: 10px;
    }
    .rq-success__text { font-size: 14px; color: #9c9488; line-height: 1.7; margin-bottom: 28px; }
    .rq-success__ref {
      display: inline-block;
      background: #f7f4ee;
      border: 1px solid #ede9e0;
      border-radius: 8px;
      padding: 10px 24px;
      font-size: 13px;
      color: #294b5a;
      font-weight: 600;
      letter-spacing: .06em;
      margin-bottom: 24px;
    }

    /* ── Side panel ── */
    .rq-side { display: flex; flex-direction: column; gap: 16px; }

    .rq-info-card {
      background: #fff;
      border-radius: 16px;
      border: 1px solid #ede9e0;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(41,75,90,.07);
    }
    .rq-info-card__head {
      padding: 16px 20px;
      background: #f7f4ee;
      border-bottom: 1px solid #ede9e0;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .rq-info-card__head-icon {
      font-size: 16px;
      width: 32px; height: 32px;
      background: linear-gradient(135deg, rgba(41,75,90,.1), rgba(211,190,118,.12));
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .rq-info-card__head-title {
      font-size: 16px;
      font-weight: 600;
      color: #294b5a;
    }
    .rq-info-card__body { padding: 20px; }

    .rq-info-list { display: flex; flex-direction: column; gap: 14px; }

    .rq-contact-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 0;
      border-bottom: 1px solid #f0ece4;
    }
    .rq-contact-item:last-child { border-bottom: none; }
    .rq-contact-item__ico {
      width: 34px; height: 34px;
      background: rgba(41,75,90,.07);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      flex-shrink: 0;
      color: #294b5a;
    }
    .rq-contact-item__label { font-size: 11px; color: #9c9488; }
    .rq-contact-item__val { font-size: 13px; font-weight: 600; color: #294b5a; margin-top: 1px; }

    .rq-badge-row { display: flex; gap: 8px; flex-wrap: wrap; }
    .rq-badge {
      display: flex;
      align-items: center;
      gap: 6px;
      background: rgba(41,75,90,.06);
      border-radius: 20px;
      padding: 5px 12px;
      font-size: 12px;
      color: #294b5a;
      font-weight: 500;
    }
    .rq-badge::before { content: '✓'; font-weight: 700; color: #d3be76; }

    /* ── AUTH MODAL STYLES ── */
    .rq-modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(26, 50, 64, 0.75);
      backdrop-filter: blur(8px);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
      animation: rqFadeIn .3s ease-out forwards;
    }
    .rq-modal {
      background: #ffffff;
      border-radius: 20px;
      max-width: 440px;
      width: 100%;
      padding: 36px 32px;
      text-align: center;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      position: relative;
      border: 1px solid rgba(211, 190, 118, 0.3);
      animation: rqScaleUp .3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
    .rq-modal__close {
      position: absolute;
      top: 16px;
      right: 16px;
      width: 32px;
      height: 32px;
      background: #f7f4ee;
      border: 1px solid #ede9e0;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #1a3240;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    .rq-modal__close:hover {
      background: #294b5a;
      color: #ffffff;
    }
    .rq-modal__icon-wrapper {
      width: 72px;
      height: 72px;
      background: linear-gradient(135deg, #1a3240 0%, #294b5a 100%);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
      box-shadow: 0 8px 20px rgba(41, 75, 90, 0.25);
      border: 2px solid #d3be76;
    }
    .rq-modal__title {
      font-size: 22px;
      font-weight: 700;
      color: #1a3240;
      margin-bottom: 10px;
      line-height: 1.2;
    }
    .rq-modal__text {
      font-size: 14px;
      color: #6b7280;
      line-height: 1.6;
      margin-bottom: 24px;
    }
    .rq-modal__actions {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .rq-modal__btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 100%;
      background: linear-gradient(90deg, #d3be76, #b09a50);
      color: #1a3240;
      font-size: 15px;
      font-weight: 700;
      padding: 14px 24px;
      border-radius: 12px;
      text-decoration: none;
      transition: all 0.25s ease;
      box-shadow: 0 4px 16px rgba(211, 190, 118, 0.35);
      border: none;
      cursor: pointer;
    }
    .rq-modal__btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(211, 190, 118, 0.5);
      color: #1a3240;
    }
    .rq-modal__btn-secondary {
      background: transparent;
      border: 1.5px solid #ede9e0;
      color: #6b7280;
      box-shadow: none;
    }
    .rq-modal__btn-secondary:hover {
      background: #f7f4ee;
      color: #1a3240;
      transform: none;
      box-shadow: none;
    }

    @keyframes rqFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes rqScaleUp {
      from { opacity: 0; transform: scale(0.92) translateY(10px); }
      to { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes rqFadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .rq-anim { animation: rqFadeUp .5s ease both; }
    .rq-anim-delay-1 { animation-delay: .08s; }
  `}</style>
);

// ── Counter ────────────────────────────────────────────────────────────────
const PaxCounter = ({ label, sub, value, onChange, min = 0 }: {
  label: string; sub: string; value: number;
  onChange: (v: number) => void; min?: number;
}) => (
  <div className="rq-passenger-row">
    <div className="rq-passenger-row__info">
      <div className="rq-passenger-row__name">{label}</div>
      <div className="rq-passenger-row__sub">{sub}</div>
    </div>
    <div className="rq-passenger-row__ctrl">
      <button className="rq-pax-btn" disabled={value <= min} onClick={() => onChange(value - 1)}>−</button>
      <span className="rq-pax-val">{value}</span>
      <button className="rq-pax-btn" disabled={value >= 9} onClick={() => onChange(value + 1)}>+</button>
    </div>
  </div>
);

// ── Composant Select personnalisé ─────────────────────────────────────────
interface SelectOption {
  value: string;
  label: string;
}

const CustomSelect = ({
  label,
  required = true,
  placeholder = "Select…",
  value,
  options,
  onChange,
  searchable = false,
  error,
}: {
  label?: string;
  required?: boolean;
  placeholder?: string;
  value: string;
  options: SelectOption[];
  onChange: (val: string) => void;
  searchable?: boolean;
  error?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value);
  const filtered = searchable && query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div className="rq-field" ref={wrapperRef}>
      {label && (
        <label className="rq-field__label">
          {label} {required && <span>*</span>}
        </label>
      )}
      <div
        className={`rq-select-trigger ${isOpen ? "open" : ""} ${!selected ? "placeholder" : ""} ${error ? "invalid" : ""}`}
        onClick={() => setIsOpen((o) => !o)}
      >
        <span>{selected ? selected.label : placeholder}</span>
        <svg className={`rq-select-chevron ${isOpen ? "open" : ""}`} viewBox="0 0 12 8" fill="none">
          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>

      {isOpen && (
        <div className="rq-select-dropdown">
          {searchable && (
            <input
              className="rq-select-search"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          )}
          <div className="rq-select-list">
            {filtered.length === 0 && <div className="rq-select-empty">No results found</div>}
            {filtered.map((o) => (
              <div
                key={o.value}
                className={`rq-select-option ${o.value === value ? "active" : ""}`}
                onClick={() => {
                  onChange(o.value);
                  setIsOpen(false);
                  setQuery("");
                }}
              >
                {o.label}
              </div>
            ))}
          </div>
        </div>
      )}
      {error && <p className="rq-field__error">{error}</p>}
    </div>
  );
};

// ── Composant AutoComplete Kayak ───────────────────────────────────────────
const KayakFlightSearchInput = ({
  label,
  placeholder,
  value,
  onChange,
  error,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (val: string) => void;
  error?: string;
}) => {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<KayakLocation[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://booking.kayak.com/mvm/smartyv2/search?f=j&s=flight&where=${encodeURIComponent(query)}`
        );
        const data = await response.json();
        setResults(Array.isArray(data) ? data : []);
        setIsOpen(true);
      } catch (error) {
        console.error("Erreur lors de la recherche des vols Kayak:", error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (item: KayakLocation) => {
    const selectedText = item.shortdisplayname || item.displayname || `${item.name} (${item.apicode})`;
    setQuery(selectedText);
    onChange(selectedText);
    setIsOpen(false);
  };

  return (
    <div className="rq-field" ref={wrapperRef}>
      <label className="rq-field__label">
        {label} <span>*</span>
      </label>
      <input
        className={`rq-field__input ${error ? "invalid" : ""}`}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
        }}
        onFocus={() => {
          if (results.length > 0) setIsOpen(true);
        }}
      />

      {isOpen && results.length > 0 && (
        <div className="kayak-dropdown">
          {results.map((item, index) => {
            const isCityHeader = item.loctype === "ct" || item.isMetroOnly || item.displayType?.type === "city";
            const isSubAirport = item.loctype === "ap" && !isCityHeader;

            return (
              <div
                key={item.id || item.objectID || index}
                className={`kayak-dropdown-item ${isSubAirport ? "kayak-sub-item" : ""}`}
                onClick={() => handleSelect(item)}
              >
                {isSubAirport && (
                  <svg className="kayak-arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                )}

                <div className="kayak-icon-box">
                  <svg viewBox="0 0 24 24">
                    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
                  </svg>
                </div>

                <div className="kayak-info">
                  <div className="kayak-title-row">
                    <span className="kayak-main-title">
                      {item.name || item.cityname}
                    </span>
                    <span className="kayak-iata-code">
                      {item.apicode || item.ap || (item.kayakId !== item.cityname ? item.kayakId : "")}
                    </span>
                  </div>
                  <div className="kayak-subtitle">
                    {isCityHeader ? (item.secondary || "All airports") : (item.cityname || item.country)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {error && <p className="rq-field__error">{error}</p>}
    </div>
  );
};

// ── État initial du formulaire ─────────────────────────────────────────────
const INITIAL_FORM = {
  firstName: "", lastName: "", email: "", phone: "",
  nationality: "", passportNumber: "", passportExpiryMonth: "", passportExpiryYear: "",
  from: "", to: "", via: "", flexibility: "exact",
  baggage: "1 bag", specialRequests: "", preferredAirline: "",
};

// ── Composant Principal ───────────────────────────────────────────────────
const Ticket = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [lastRef, setLastRef] = useState("");

  // Verification spontanee au premier chargement de la page
  useEffect(() => {
    const token = localStorage.getItem("token_customer_silverjarz");
    if (!token) {
      setShowAuthModal(true);
    }
  }, []);

  // Configuration Trip
  const [tripType, setTripType] = useState("roundtrip");
  const [tripClass, setTripClass] = useState("Economy");

  // Dates
  const [departDate, setDepartDate] = useState<DateObject | null>(new DateObject().setDay(20));
  const [returnDate, setReturnDate] = useState<DateObject | null>(new DateObject().setDay(5).add(1, "month"));

  // Passengers
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);

  // Form State
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [termsAccepted, setTermsAccepted] = useState(false);

  // ── Validation state ──────────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<TextFieldName | "departDate" | "returnDate" | "terms", string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = (key: string, val: string) => {
    setForm((f) => ({ ...f, [key]: val }));
    setFieldErrors((prev) => {
      const name = key as TextFieldName;
      if (!prev[name]) return prev;
      const errorKey = validateField(name, val);
      return { ...prev, [name]: errorKey ? t(errorKey) : "" };
    });
  };

  const handleDepartDateChange = (d: DateObject) => {
    setDepartDate(d);
    setFieldErrors((prev) => (prev.departDate ? { ...prev, departDate: d ? "" : prev.departDate } : prev));
  };

  const handleReturnDateChange = (d: DateObject) => {
    setReturnDate(d);
    setFieldErrors((prev) => (prev.returnDate ? { ...prev, returnDate: d ? "" : prev.returnDate } : prev));
  };

  const handleTermsChange = (checked: boolean) => {
    setTermsAccepted(checked);
    setFieldErrors((prev) => (prev.terms ? { ...prev, terms: checked ? "" : prev.terms } : prev));
  };

  // Valide les champs requis pour l'étape 1
  const validateStep1 = (): boolean => {
    const errors: typeof fieldErrors = {};
    STEP1_FIELDS.forEach((name) => {
      const errorKey = validateField(name, form[name]);
      errors[name] = errorKey ? t(errorKey) : "";
    });
    errors.departDate = departDate ? "" : t("errorDepartureDate");
    if (tripType !== "oneway") {
      errors.returnDate = returnDate ? "" : t("errorReturnDate");
    }

    setFieldErrors((prev) => ({ ...prev, ...errors }));
    const hasError = Object.values(errors).some(Boolean);
    setFormError(hasError ? t("errorCompleteFields") : null);
    return !hasError;
  };

  // Valide les champs requis pour l'étape 2
  const validateStep2 = (): boolean => {
    const errors: typeof fieldErrors = {};
    STEP2_FIELDS.forEach((name) => {
      const errorKey = validateField(name, form[name]);
      errors[name] = errorKey ? t(errorKey) : "";
    });

    if (!errors.passportExpiryMonth && !errors.passportExpiryYear) {
      const expiry = new Date(Number(form.passportExpiryYear), Number(form.passportExpiryMonth) - 1, 1);
      const now = new Date();
      now.setDate(1);
      now.setHours(0, 0, 0, 0);
      if (expiry < now) {
        errors.passportExpiryYear = t("errorPassportExpired");
      }
    }

    setFieldErrors((prev) => ({ ...prev, ...errors }));
    const hasError = Object.values(errors).some(Boolean);
    setFormError(hasError ? t("errorCompleteFields") : null);
    return !hasError;
  };

  // Valide les champs requis pour l'étape 3
  const validateStep3 = (): boolean => {
    const errors: typeof fieldErrors = {};
    STEP3_FIELDS.forEach((name) => {
      const errorKey = validateField(name, form[name]);
      errors[name] = errorKey ? t(errorKey) : "";
    });
    errors.terms = termsAccepted ? "" : t("errorAcceptTerms");

    setFieldErrors((prev) => ({ ...prev, ...errors }));
    const hasError = Object.values(errors).some(Boolean);
    setFormError(hasError ? t("errorCompleteFields") : null);
    return !hasError;
  };

  // Réinitialise le formulaire
  const resetForm = () => {
    setForm({ ...INITIAL_FORM });
    setTripType("roundtrip");
    setTripClass("Economy");
    setDepartDate(new DateObject().setDay(20));
    setReturnDate(new DateObject().setDay(5).add(1, "month"));
    setAdults(1);
    setChildren(0);
    setInfants(0);
    setTermsAccepted(false);
    setFieldErrors({});
    setFormError(null);
    setStep(1);
    setSubmitted(false);
  };

  const handleProtectedAction = (action: () => void) => {
    const token = localStorage.getItem("token_customer_silverjarz");
    if (!token) {
      setShowAuthModal(true);
    } else {
      action();
    }
  };

  const goToStep2 = () => {
    if (validateStep1()) {
      handleProtectedAction(() => setStep(2));
    }
  };

  const goToStep3 = () => {
    if (validateStep2()) {
      handleProtectedAction(() => setStep(3));
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const token = localStorage.getItem("token_customer_silverjarz");
    const passportExpiry =
      form.passportExpiryMonth && form.passportExpiryYear
        ? `${form.passportExpiryMonth}/${form.passportExpiryYear}`
        : "";

    const payload = {
      tripType,
      tripClass,
      from: form.from,
      to: form.to,
      via: form.via,
      departDate: departDate ? departDate.format("YYYY-MM-DD") : null,
      returnDate: returnDate ? returnDate.format("YYYY-MM-DD") : null,
      flexibility: form.flexibility,
      baggage: form.baggage,
      preferredAirline: form.preferredAirline,
      adults,
      children,
      infants,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      nationality: form.nationality,
      passportNumber: form.passportNumber,
      passportExpiry,
      specialRequests: form.specialRequests,
    };
  
    try {
      const response = await fetch(`${API_URL}/flight-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });
  
      const data = await response.json();
  
      if (response.ok) {
        setLastRef(`TKT-${Math.random().toString(36).substring(2, 8).toUpperCase()}`);
        setSubmitted(true);
      } else {
        console.error("Erreur de validation:", data);
        setFormError(data?.message || "The server rejected the request. Please check your details and try again.");
      }
    } catch (error) {
      console.error("Erreur réseau:", error);
      setFormError("Failed to connect to the server. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitRequest = () => {
    if (isSubmitting) return;
    if (validateStep3()) {
      handleProtectedAction(handleSubmit);
    }
  };

  return (
    <>
      <PageStyles />

      {/* ── AUTH MODAL ── */}
      {showAuthModal && (
        <div className="rq-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="rq-modal" onClick={(e) => e.stopPropagation()}>
            <button className="rq-modal__close" onClick={() => setShowAuthModal(false)} aria-label="Close modal">
              ✕
            </button>
            <div className="rq-modal__icon-wrapper">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d3be76" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <h3 className="rq-modal__title">{t("authRequired")}</h3>
            <p className="rq-modal__text">{t("authRequiredText")}</p>
            <div className="rq-modal__actions">
              <a href="/login" className="rq-modal__btn">
                <span>{t("signInToContinue")}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </a>
              <button className="rq-modal__btn rq-modal__btn-secondary" onClick={() => setShowAuthModal(false)}>
                {t("continueBrowsing")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rq-page">
        <div className="header-margin" />
        <Header />

        {/* ── HERO ── */}
        <section className="rq-hero">
          <div className="rq-hero__decor">✈</div>
          <div className="container">
            <div className="rq-hero__breadcrumb">
              <a href="/">{t("ticketBreadcrumbHome")}</a> › <a href="#">{t("ticketBreadcrumbTravel")}</a> › <span>{t("ticketBreadcrumbCurrent")}</span>
            </div>
            <div className="rq-hero__eyebrow">{t("ticketBreadcrumbTravel")}</div>
            <h1 className="rq-hero__title">
              {t("ticketPageTitle")}
            </h1>
            <p className="rq-hero__sub">
              {t("ticketPageSubtitle")}
            </p>
          </div>
        </section>

        {/* ── LAYOUT ── */}
        <section style={{ paddingBottom: 0 }}>
          <div className="container">
            <div className="rq-layout">

              {/* ── FORM ── */}
              <div className="rq-anim">
                <div className="rq-card">
                  <div className="rq-card__head">
                    <div className="rq-card__head-icon">✈</div>
                    <div>
                      <div className="rq-card__head-title">{t("ticketCardTitle")}</div>
                      <div className="rq-card__head-sub">{t("ticketCardSubtitle")}</div>
                    </div>
                  </div>

                  {submitted ? (
                    <div className="rq-success">
                      <div className="rq-success__icon" style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
                        <svg
                          width="64"
                          height="64"
                          viewBox="0 0 64 64"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="32" cy="32" r="32" fill="#294b5a" opacity="0.1" />
                          <circle cx="32" cy="32" r="24" fill="#294b5a" />
                          <path
                            d="M23 32.5L29 38.5L41 25.5"
                            stroke="#d3be76"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="rq-success__title">{t("requestSubmitted")}</div>
                      <p className="rq-success__text">
                        {t("requestSubmittedText")} <strong>24 {t("hours")}</strong> {t("toConfirm")}
                      </p>
                      <div className="rq-success__ref">
                        {t("reference")}: <span style={{ color: "#d3be76" }}>{lastRef}</span>
                      </div>
                      <button
                        onClick={resetForm}
                        style={{
                          background: "#294b5a", color: "#fff", border: "none",
                          borderRadius: 10, padding: "12px 32px", fontSize: 14,
                          fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit', sans-serif",
                        }}
                      >{t("submitAnother")}</button>
                    </div>
                  ) : (
                    <div className="rq-card__body">

                      {/* Steps */}
                      <div className="rq-steps">
                        {[
                          { n: 1, label: t("ticketStep1") },
                          { n: 2, label: t("ticketStep2") },
                          { n: 3, label: t("ticketStep3") },
                        ].map((s, i, arr) => (
                          <div key={s.n} className="rq-step" style={{ flex: i < arr.length - 1 ? 1 : "none" }}>
                            <div className={`rq-step__num ${step > s.n ? "done" : step === s.n ? "active" : "pending"}`}>
                              {step > s.n ? "✓" : s.n}
                            </div>
                            <span className={`rq-step__label ${step > s.n ? "done" : step === s.n ? "active" : "pending"}`}>
                              {s.label}
                            </span>
                            {i < arr.length - 1 && (
                              <div className={`rq-step__line ${step > s.n ? "done" : ""}`} />
                            )}
                          </div>
                        ))}
                      </div>

                      {formError && <div className="rq-form-alert">{formError}</div>}

                      {/* ── STEP 1: Trip details ── */}
                      {step === 1 && (
                        <>
                          <div className="rq-section">
                            <div className="rq-section__bar" />
                            <div className="rq-section__title">{t("tripConfigTitle")}</div>
                          </div>

                          <div className="rq-field rq-mb">
                            <span className="rq-field__label">{t("tripTypeLabel")} <span>*</span></span>
                            <div className="rq-toggle">
                              {["roundtrip", "oneway", "multicity"].map((trip) => (
                                <button
                                  key={trip}
                                  className={`rq-toggle__btn ${tripType === trip ? "active" : ""}`}
                                  onClick={() => {
                                    setTripType(trip);
                                    if (trip === "oneway") {
                                      setFieldErrors((prev) => ({ ...prev, returnDate: "" }));
                                    }
                                  }}
                                >
                                  {trip === "roundtrip" ? `⇄  ${t("tripTypeRoundtrip")}` : trip === "oneway" ? `→  ${t("tripTypeOneway")}` : `⊕  ${t("tripTypeMulticity")}`}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="rq-section">
                            <div className="rq-section__bar" />
                            <div className="rq-section__title">{t("routeTitle")}</div>
                          </div>

                          <div className="rq-grid-2 rq-mb">
                            <KayakFlightSearchInput
                              label={t("flyingFrom")}
                              placeholder={t("flyingFromPlaceholder")}
                              value={form.from}
                              onChange={(val) => set("from", val)}
                              error={fieldErrors.from}
                            />
                            <KayakFlightSearchInput
                              label={t("flyingTo")}
                              placeholder={t("flyingToPlaceholder")}
                              value={form.to}
                              onChange={(val) => set("to", val)}
                              error={fieldErrors.to}
                            />
                          </div>

                          {tripType === "multicity" && (
                            <div className="rq-mb">
                              <KayakFlightSearchInput
                                label={t("viaStopover")}
                                placeholder={t("viaPlaceholder")}
                                value={form.via}
                                onChange={(val) => set("via", val)}
                              />
                            </div>
                          )}

                          <div className={`rq-mb ${tripType === "oneway" ? "" : "rq-grid-2"}`}>
                            <div className="rq-field">
                              <label className="rq-field__label">{t("departureDate")} <span>*</span></label>
                              <DatePicker
                                inputClass={`custom_input-picker ${fieldErrors.departDate ? "invalid" : ""}`}
                                containerClassName="custom_container-picker"
                                value={departDate}
                                onChange={handleDepartDateChange}
                                format="MMMM DD, YYYY"
                                offsetY={10}
                              />
                              {fieldErrors.departDate && <p className="rq-field__error">{fieldErrors.departDate}</p>}
                            </div>
                            {tripType !== "oneway" && (
                              <div className="rq-field">
                                <label className="rq-field__label">{t("returnDate")} <span>*</span></label>
                                <DatePicker
                                  inputClass={`custom_input-picker ${fieldErrors.returnDate ? "invalid" : ""}`}
                                  containerClassName="custom_container-picker"
                                  value={returnDate}
                                  onChange={handleReturnDateChange}
                                  format="MMMM DD, YYYY"
                                  offsetY={10}
                                />
                                {fieldErrors.returnDate && <p className="rq-field__error">{fieldErrors.returnDate}</p>}
                              </div>
                            )}
                          </div>

                          <div className="rq-field rq-mb">
                            <label className="rq-field__label">{t("dateFlexibility")}</label>
                            <select className="rq-field__select" value={form.flexibility}
                              onChange={(e) => set("flexibility", e.target.value)}>
                              <option value="exact">{t("dateFlexibilityExact")}</option>
                              <option value="1day">{t("dateFlexibility1Day")}</option>
                              <option value="3days">{t("dateFlexibility3Days")}</option>
                              <option value="1week">{t("dateFlexibility1Week")}</option>
                              <option value="flexible">{t("dateFlexibilityFlexible")}</option>
                            </select>
                          </div>

                          <div className="rq-grid-2 rq-mb">
                            <div className="rq-field">
                              <label className="rq-field__label">{t("baggagePreference")}</label>
                              <select className="rq-field__select" value={form.baggage}
                                onChange={(e) => set("baggage", e.target.value)}>
                                <option value="1 bag">{t("baggage1Bag")}</option>
                                <option value="2 bags">{t("baggage2Bags")}</option>
                                <option value="premium">{t("baggagePremium")}</option>
                                <option value="cabin only">{t("baggageCabinOnly")}</option>
                              </select>
                            </div>
                            <div className="rq-field">
                              <label className="rq-field__label">{t("preferredAirline")}</label>
                              <input className="rq-field__input" placeholder={t("preferredAirlinePlaceholder")}
                                value={form.preferredAirline} onChange={(e) => set("preferredAirline", e.target.value)} />
                            </div>
                          </div>

                          <button className="rq-submit" onClick={goToStep2}>
                            {t("continueToPassengers")}
                          </button>
                        </>
                      )}

                      {/* ── STEP 2: Passengers ── */}
                      {step === 2 && (
                        <>
                          <div className="rq-section">
                            <div className="rq-section__bar" />
                            <div className="rq-section__title">{t("passengersTitle")}</div>
                          </div>

                          <div style={{ marginBottom: 24 }}>
                            <PaxCounter label={t("adults")} sub={t("adultsSub")} value={adults} onChange={setAdults} min={1} />
                            <PaxCounter label={t("children")} sub={t("childrenSub")} value={children} onChange={setChildren} />
                            <PaxCounter label={t("infants")} sub={t("infantsSub")} value={infants} onChange={setInfants} />
                          </div>

                          <div className="rq-divider" />

                          <div className="rq-section">
                            <div className="rq-section__bar" />
                            <div className="rq-section__title">{t("leadPassengerTitle")}</div>
                          </div>

                          <div className="rq-grid-2 rq-mb">
                            <div className="rq-field">
                              <label className="rq-field__label">{t("firstName")} <span>*</span></label>
                              <input className={`rq-field__input ${fieldErrors.firstName ? "invalid" : ""}`} placeholder={t("firstNamePlaceholder")}
                                value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
                              {fieldErrors.firstName && <p className="rq-field__error">{fieldErrors.firstName}</p>}
                            </div>
                            <div className="rq-field">
                              <label className="rq-field__label">{t("lastName")} <span>*</span></label>
                              <input className={`rq-field__input ${fieldErrors.lastName ? "invalid" : ""}`} placeholder={t("lastNamePlaceholder")}
                                value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
                              {fieldErrors.lastName && <p className="rq-field__error">{fieldErrors.lastName}</p>}
                            </div>
                          </div>

                          <div className="rq-grid-3 rq-mb">
                            <CustomSelect
                              label={t("nationality")}
                              placeholder={t("nationalityPlaceholder")}
                              value={form.nationality}
                              options={NATIONALITY_OPTIONS}
                              onChange={(v) => set("nationality", v)}
                              searchable
                              error={fieldErrors.nationality}
                            />
                            <div className="rq-field">
                              <label className="rq-field__label">{t("passportNumber")} <span>*</span></label>
                              <input className={`rq-field__input ${fieldErrors.passportNumber ? "invalid" : ""}`} placeholder={t("passportNumberPlaceholder")}
                                value={form.passportNumber} onChange={(e) => set("passportNumber", e.target.value)} />
                              {fieldErrors.passportNumber && <p className="rq-field__error">{fieldErrors.passportNumber}</p>}
                            </div>
                            <div className="rq-field">
                              <label className="rq-field__label">{t("passportExpiry")} <span>*</span></label>
                              <div className="rq-expiry-row">
                                <CustomSelect
                                  required={false}
                                  placeholder={t("passportExpiryMonth")}
                                  value={form.passportExpiryMonth}
                                  options={MONTH_OPTIONS}
                                  onChange={(v) => set("passportExpiryMonth", v)}
                                  error={fieldErrors.passportExpiryMonth}
                                />
                                <CustomSelect
                                  required={false}
                                  placeholder={t("passportExpiryYear")}
                                  value={form.passportExpiryYear}
                                  options={YEAR_OPTIONS}
                                  onChange={(v) => set("passportExpiryYear", v)}
                                  error={fieldErrors.passportExpiryYear}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="rq-field rq-mb">
                            <label className="rq-field__label">{t("specialRequests")}</label>
                            <textarea className="rq-field__textarea"
                              placeholder={t("specialRequestsPlaceholder")}
                              value={form.specialRequests}
                              onChange={(e) => set("specialRequests", e.target.value)}
                            />
                          </div>

                          <div style={{ display: "flex", gap: 12 }}>
                            <button
                              onClick={() => setStep(1)}
                              style={{
                                flex: "0 0 auto", padding: "16px 24px",
                                background: "#f0ece4", color: "#294b5a",
                                border: "none", borderRadius: 12,
                                fontFamily: "'Outfit', sans-serif",
                                fontSize: 14, fontWeight: 600, cursor: "pointer",
                              }}
                            >← {t("back")}</button>
                            <button className="rq-submit" style={{ flex: 1 }} onClick={goToStep3}>
                              {t("continueToPassengers")}
                            </button>
                          </div>
                        </>
                      )}

                      {/* ── STEP 3: Contact ── */}
                      {step === 3 && (
                        <>
                          <div className="rq-section">
                            <div className="rq-section__bar" />
                            <div className="rq-section__title">{t("contactTitle")}</div>
                          </div>

                          <div className="rq-grid-2 rq-mb">
                            <div className="rq-field">
                              <label className="rq-field__label">{t("emailAddress")} <span>*</span></label>
                              <input className={`rq-field__input ${fieldErrors.email ? "invalid" : ""}`} type="email" placeholder={t("emailPlaceholder")}
                                value={form.email} onChange={(e) => set("email", e.target.value)} />
                              {fieldErrors.email && <p className="rq-field__error">{fieldErrors.email}</p>}
                            </div>
                            <div className="rq-field">
                              <label className="rq-field__label">{t("phoneNumber")} <span>*</span></label>
                              <input className={`rq-field__input ${fieldErrors.phone ? "invalid" : ""}`} type="tel" placeholder={t("phonePlaceholder")}
                                value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                              {fieldErrors.phone && <p className="rq-field__error">{fieldErrors.phone}</p>}
                            </div>
                          </div>

                          <div className="rq-divider" />
                          <div className="rq-section">
                            <div className="rq-section__bar" />
                            <div className="rq-section__title">{t("requestSummary")}</div>
                          </div>

                          <div style={{
                            background: "#f7f4ee", borderRadius: 12, padding: "18px 20px",
                            marginBottom: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px",
                          }}>
                            {[
                              { label: t("route"), val: `${form.from || "—"} → ${form.to || "—"}` },
                              { label: t("tripTypeLabel"), val: tripType === "roundtrip" ? t("tripTypeRoundtrip") : tripType === "oneway" ? t("tripTypeOneway") : t("tripTypeMulticity") },
                              { label: t("departure"), val: departDate?.format("MMM DD, YYYY") || "—" },
                              { label: t("return"), val: tripType !== "oneway" ? (returnDate?.format("MMM DD, YYYY") || "—") : "N/A" },
                              { label: t("passengers"), val: `${adults} ${t("adults")}${adults > 1 ? "s" : ""}${children > 0 ? `, ${children} ${t("children")}${children > 1 ? "ren" : ""}` : ""}${infants > 0 ? `, ${infants} ${t("infants")}${infants > 1 ? "s" : ""}` : ""}` },
                              { label: t("cabin"), val: tripClass },
                              { label: t("baggage"), val: form.baggage || "1 bag" },
                              { label: t("leadPassenger"), val: form.firstName ? `${form.firstName} ${form.lastName}` : "—" },
                            ].map((r) => (
                              <div key={r.label}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "#9c9488", letterSpacing: ".1em", marginBottom: 3 }}>{r.label}</div>
                                <div style={{ fontSize: 14, fontWeight: 600, color: "#294b5a" }}>{r.val}</div>
                              </div>
                            ))}
                          </div>

                          <div className={`rq-terms-row ${fieldErrors.terms ? "invalid" : ""}`} style={{
                            display: "flex", alignItems: "flex-start", gap: 10,
                            marginBottom: 24, fontSize: 13, color: "#6b6560",
                          }}>
                            <input
                              type="checkbox"
                              checked={termsAccepted}
                              onChange={(e) => handleTermsChange(e.target.checked)}
                              style={{ marginTop: 2, accentColor: "#294b5a", flexShrink: 0, width: 15, height: 15 }}
                            />
                            <span>{t("termsConfirm")} <a href="#" style={{ color: "#294b5a", fontWeight: 600 }}>{t("termsConditions")}</a> {t("and")} <a href="#" style={{ color: "#294b5a", fontWeight: 600 }}>{t("privacyPolicy")}</a>.</span>
                          </div>
                          {fieldErrors.terms && <p className="rq-field__error" style={{ marginTop: -16, marginBottom: 20 }}>{fieldErrors.terms}</p>}

                          <div style={{ display: "flex", gap: 12 }}>
                            <button
                              onClick={() => setStep(2)}
                              disabled={isSubmitting}
                              style={{
                                flex: "0 0 auto", padding: "16px 24px",
                                background: "#f0ece4", color: "#294b5a",
                                border: "none", borderRadius: 12,
                                fontFamily: "'Outfit', sans-serif",
                                fontSize: 14, fontWeight: 600,
                                cursor: isSubmitting ? "not-allowed" : "pointer",
                                opacity: isSubmitting ? 0.6 : 1,
                              }}
                            >← {t("back")}</button>
                            <button className="rq-submit" style={{ flex: 1 }} onClick={submitRequest} disabled={isSubmitting}>
                              {isSubmitting ? (
                                <>
                                  <span
                                    style={{
                                      width: 16,
                                      height: 16,
                                      border: "2px solid rgba(26,50,64,.35)",
                                      borderTopColor: "#1a3240",
                                      borderRadius: "50%",
                                      display: "inline-block",
                                      animation: "rqSpin .7s linear infinite",
                                    }}
                                  />
                                  {t("submitting")}
                                </>
                              ) : (
                                <>✈ {t("submitRequest")}</>
                              )}
                            </button>
                          </div>
                        </>
                      )}

                    </div>
                  )}
                </div>
              </div>

              {/* ── SIDE PANEL ── */}
              <div className="rq-side rq-anim rq-anim-delay-1">

                <div className="rq-info-card">
                  <div className="rq-info-card__head">
                    <div className="rq-info-card__head-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                      </svg>
                    </div>
                    <div className="rq-info-card__head-title">{t("howItWorks")}</div>
                  </div>
                  <div className="rq-info-card__body">
                    <div className="rq-info-list">
                      {[
                        { num: "01", title: t("step1TitleFlight"), text: t("step1Text") },
                        { num: "02", title: t("step2TitleFlight"), text: t("step2Text") },
                        { num: "03", title: t("step3TitleFlight"), text: t("step3Text") },
                        { num: "04", title: t("step4TitleFlight"), text: t("step4Text") },
                      ].map((s) => (
                        <div key={s.num} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                          <div style={{
                            width: 28, height: 28, background: "linear-gradient(135deg, #294b5a, #3a6272)",
                            color: "#d3be76", borderRadius: 7, display: "flex", alignItems: "center",
                            justifyContent: "center", fontSize: 10, fontWeight: 800,
                            flexShrink: 0, letterSpacing: ".04em",
                          }}>{s.num}</div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#294b5a", marginBottom: 2 }}>{s.title}</div>
                            <div style={{ fontSize: 12, color: "#9c9488", lineHeight: 1.5 }}>{s.text}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rq-info-card">
                  <div className="rq-info-card__head">
                    <div className="rq-info-card__head-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                        <path d="M12 8v4"/>
                        <path d="M12 16h.01"/>
                      </svg>
                    </div>
                    <div className="rq-info-card__head-title">{t("ourGuarantees")}</div>
                  </div>
                  <div className="rq-info-card__body">
                    <div className="rq-badge-row">
                      {[t("guaranteeBestPrice"), t("guaranteeFreeCancellation"), t("guaranteeSecurePayment"), t("guaranteeSupport"), t("guaranteeAirlines"), t("guaranteeETicket")].map((b) => (
                        <div key={b} className="rq-badge">{b}</div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rq-info-card">
                  <div className="rq-info-card__head">
                    <div className="rq-info-card__head-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                    </div>
                    <div className="rq-info-card__head-title">{t("needAssistance")}</div>
                  </div>
                  <div className="rq-info-card__body">
                    <div className="rq-contact-item">
                      <div className="rq-contact-item__ico">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                      </div>
                      <div>
                        <div className="rq-contact-item__label">{t("phone")}</div>
                        <div className="rq-contact-item__val">+968 9357 9571</div>
                      </div>
                    </div>

                    <div className="rq-contact-item">
                      <div className="rq-contact-item__ico">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                      </div>
                      <div>
                        <div className="rq-contact-item__label">{t("email")}</div>
                        <div className="rq-contact-item__val">info@silverjarz.com</div>
                      </div>
                    </div>

                    <div className="rq-contact-item">
                      <div className="rq-contact-item__ico">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                      </div>
                      <div>
                        <div className="rq-contact-item__label">{t("hoursLabel")}</div>
                        <div className="rq-contact-item__val">{t("hoursValue")}</div>
                      </div>
                    </div>

                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        <DefaultFooter />
      </div>
    </>
  );
};

export default Ticket;